import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { valueByName } from './form_utils';

const PREFIX = 'ECQuillEditor';

const classes = {
  root: `${PREFIX}-root`,
  stepButton: `${PREFIX}-stepButton`,
};

const StyledDiv = styled('div')(({ theme }) => ({
  [`& .${classes.root}`]: {
    '& .ql-toolbar': {
      borderLeft: 'none',
      borderTop: 'none',
      borderRight: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
      '& .ql-picker-label:hover': {
        color: theme.palette.secondary.main,
      },
      '& .ql-picker-label.ql-active': {
        color: theme.palette.secondary.main,
      },
      '& .ql-picker-item:hover': {
        color: theme.palette.secondary.main,
      },
      '& .ql-picker-item.ql-selected': {
        color: theme.palette.secondary.main,
      },
      '& button:hover': {
        color: theme.palette.secondary.main,
        '& .ql-stroke': {
          stroke: theme.palette.secondary.main,
        },
      },
      '& button:focus': {
        color: theme.palette.secondary.main,
        '& .ql-stroke': {
          stroke: theme.palette.secondary.main,
        },
      },
      '& button.ql-active': {
        '& .ql-stroke': {
          stroke: theme.palette.secondary.main,
        },
      },
      '& .ql-stroke': {
        stroke: theme.palette.text.primary,
      },
      '& .ql-picker': {
        color: theme.palette.text.primary,
      },
      '& .ql-picker-options': {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        border: 'none',
        boxShadow: theme.shadows[10],
        borderRadius: theme.shape.borderRadius,
      },
    },
    '& .ql-container': {
      border: 'none',
      '& .ql-editor': {
        fontFamily: theme.typography.fontFamily,
        fontSize: 16,
        color: theme.palette.text.primary,
        '&.ql-blank::before': {
          color: theme.palette.text.secondary,
        },
      },
    },
  },

  [`& .${classes.stepButton}`]: {
    '& + &': {
      marginLeft: theme.spacing(2),
    },
  },
}));

export const ECQuillEditor = props => {
  const { name, className, label, form, initial, defaultValue, formats, ...rest } = props;

  const [value, setValue] = useState(valueByName(initial, name) || defaultValue || '');
  const [error, setError] = useState(false);

  useEffect(() => {
    form.register(name);
    form.setValue(name, value);
  }, [form.register]);

  const transformCodeBlocks = html => {
    html = html.replace(/&nbsp;/g, ' ');
    return html.replace(
      /<div class="ql-code-block-container"[^>]*>((?:<div class="ql-code-block">[\s\S]*?<\/div>)*)<\/div>/g,
      (match, content) => {
        const lines = [];
        const lineRegex = /<div class="ql-code-block">([\s\S]*?)<\/div>/g;
        let lineMatch;
        while ((lineMatch = lineRegex.exec(content)) !== null) {
          lines.push(lineMatch[1]);
        }
        return `<pre class="ql-syntax" spellcheck="false">${lines.join('\n')}\n</pre>`;
      }
    );
  };

  const handleChange = value => {
    form.setValue(name, transformCodeBlocks(value));
    setValue(value);
    form.clearErrors(name);
  };

  const tg_modules = {
    toolbar: [['bold', 'italic', 'underline', 'strike'], ['link'], ['code-block']],
  };

  const tg_formats = ['bold', 'italic', 'underline', 'strike', 'link', 'code-block'];

  return (
    <StyledDiv className={clsx(classes.root, className)}>
      <ReactQuill
        {...rest}
        value={value}
        theme='snow'
        modules={formats === 'tg' ? tg_modules : undefined}
        formats={formats === 'tg' ? tg_formats : undefined}
        onChange={handleChange}
      />
    </StyledDiv>
  );
};
